import { useEffect, useState } from 'react';
import {
  TELEMETRY_WS_PATH,
  serverTelemetryMessageSchema,
  type MonitorState,
} from '@netdeck/contracts';
import { refreshAccessToken } from '../../shared/api/client';
import { authStore } from '../../shared/api/auth-store';

/** Что известно о статусах прямо сейчас. */
export interface LiveStatus {
  /** Состояние по идентификатору монитора. */
  monitors: Map<string, MonitorState>;
  /** Соединение установлено и подписка подтверждена снимком. */
  connected: boolean;
}

/** Переподключение: 1, 2, 4 … 30 с — как в агенте, только предел меньше. */
const RECONNECT_MIN_MS = 1000;
const RECONNECT_MAX_MS = 30_000;

/**
 * Живые статусы площадки по WebSocket (§8.3).
 *
 * Снимок приходит один раз при подписке, дальше — только дельты. Опрос
 * по таймеру не годится: §11 требует показывать смену статуса не позже
 * секунды после события, а опрос с таким периодом на площадке в тысячу
 * мониторов — это тысяча строк из БД каждую секунду на каждого зрителя.
 *
 * При обрыве соединение восстанавливается само, и снимок приходит заново.
 * Без этого экран продолжал бы показывать статусы, которые давно неверны, —
 * а именно из-за такой картинки инженер поедет на площадку зря.
 */
export function useLiveStatus(siteId: string | null): LiveStatus {
  const [monitors, setMonitors] = useState<Map<string, MonitorState>>(new Map());
  const [connected, setConnected] = useState(false);

  useEffect(() => {
    setMonitors(new Map());
    setConnected(false);

    if (siteId === null) {
      return;
    }

    let socket: WebSocket | null = null;
    let reconnectTimer: number | undefined;
    let delay = RECONNECT_MIN_MS;
    let stopped = false;

    const reconnect = (): void => {
      if (stopped) {
        return;
      }

      reconnectTimer = window.setTimeout(connect, delay);
      delay = Math.min(delay * 2, RECONNECT_MAX_MS);
    };

    const connect = (): void => {
      if (stopped) {
        return;
      }

      const token = authStore.token;

      // Токена может не быть мгновение — например, пока идёт ротация.
      // Молча сдаться здесь значит остаться без живых статусов навсегда:
      // повторить попытку будет уже некому, и экран так и застынет
      // на «нет связи», хотя связь есть.
      if (token === null) {
        reconnect();
        return;
      }

      /** Дошло ли дело до установленного соединения. */
      let opened = false;

      const scheme = window.location.protocol === 'https:' ? 'wss' : 'ws';
      socket = new WebSocket(
        `${scheme}://${window.location.host}${TELEMETRY_WS_PATH}?token=${encodeURIComponent(token)}`,
      );

      socket.addEventListener('open', () => {
        opened = true;

        // Пока шло рукопожатие, экран могли покинуть. Подписываться
        // на площадку ради соединения, которое сейчас закроется, незачем.
        if (stopped) {
          return;
        }

        socket?.send(JSON.stringify({ type: 'subscribe', siteId }));
      });

      socket.addEventListener('message', (event: MessageEvent<string>) => {
        // Сообщения сервера разбираются схемой: §6.1 не делает исключения
        // для собственного бэкенда, а расхождение версий должно быть видно.
        const parsed = serverTelemetryMessageSchema.safeParse(safeJson(event.data));
        if (!parsed.success) {
          return;
        }

        const message = parsed.data;

        if (message.type === 'snapshot') {
          delay = RECONNECT_MIN_MS;
          setMonitors(new Map(message.monitors.map((state) => [state.monitorId, state])));
          setConnected(true);
          return;
        }

        if (message.type === 'status') {
          setMonitors((previous) => {
            const next = new Map(previous);
            for (const change of message.changes) {
              next.set(change.monitorId, {
                monitorId: change.monitorId,
                nodeId: change.nodeId,
                addressId: change.addressId,
                status: change.status,
                rttMs: change.rttMs,
                since: change.at,
              });
            }
            return next;
          });
        }
      });

      socket.addEventListener('close', () => {
        setConnected(false);

        if (stopped) {
          return;
        }

        // Рукопожатие даже не состоялось — возможно, токен просрочен.
        // Обновить его больше неоткуда: в адресе сокета он предъявляется
        // один раз, и отказ — единственный сигнал. Сейчас токен держит
        // свежим опрос состояния платформы раз в десять секунд, но
        // полагаться на чужой таймер значит однажды потерять статусы
        // молча, когда этот опрос уберут.
        if (!opened) {
          void refreshAccessToken().finally(reconnect);
          return;
        }

        reconnect();
      });
    };

    connect();

    return () => {
      stopped = true;
      window.clearTimeout(reconnectTimer);
      closeWhenReady(socket);
    };
  }, [siteId]);

  return { monitors, connected };
}

/**
 * Закрытие сокета, который мог ещё не открыться.
 *
 * Оборванное на середине рукопожатие браузер считает несостоявшимся
 * соединением и пишет в консоль «can't establish a connection» — хотя
 * ошибки нет: это мы сами передумали. В разработке такое случается
 * на каждом входе в раздел, потому что React намеренно вызывает эффект
 * дважды, и первый сокет закрывается через несколько миллисекунд после
 * создания.
 *
 * Дело не только в шуме в консоли: сервер к этому моменту уже принимает
 * апгрейд и заводит клиента, который тут же умирает. Дождаться открытия
 * и закрыть по-человечески дешевле, чем разбираться с обеими половинами
 * оборванного рукопожатия.
 */
function closeWhenReady(socket: WebSocket | null): void {
  if (!socket) {
    return;
  }

  if (socket.readyState === WebSocket.CONNECTING) {
    socket.addEventListener('open', () => socket.close());
    return;
  }

  socket.close();
}

function safeJson(raw: string): unknown {
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}
